import { useState } from "react";
import { Gate, ready, Panel, Kpi, useLoad, cad, stamp, Chip, Filters } from "@/components/console/ui";
import { fmtDuration } from "@/components/SentenceClock";

const TYPE_LABEL = { signup: "join", ask: "ask", answer: "answer", undertaking: "delegate", sent_up: "proof", completed: "done", payment: "pay", cashout: "cash", flag: "feature" };

export function ActivityFeed({ limit = 60, compact, testId = "activity-feed" }) {
  const [feed] = useLoad(`/console/activity?limit=${limit}`, 10000);
  const [type, setType] = useState("");
  const rows = (ready(feed) ? feed : []).filter((e) => !type || e.type === type);
  return (
    <div data-testid={testId}>
      {!compact && (
        <div className="mb-4">
          <Filters testId="activity-filter" value={type} onChange={setType}
            options={[["", "all"], ...Object.entries(TYPE_LABEL)]} />
        </div>
      )}
      {!ready(feed) ? <Gate data={feed} testId={testId} /> : !rows.length ? <p className="text-xs opacity-60" data-testid={`${testId}-empty`}>no activity yet</p> : (
        <ol className="flex flex-col">
          {rows.map((e, i) => (
            <li key={i} className="grid grid-cols-[92px_74px_1fr] gap-3 py-2 text-xs" style={{ borderBottom: "1px dashed var(--border)" }} data-testid={`${testId}-item-${i}`}>
              <span className="opacity-60 whitespace-nowrap">{stamp(e.at)}</span>
              <span><Chip on={["payment", "sent_up", "cashout"].includes(e.type)}>{TYPE_LABEL[e.type]}</Chip></span>
              <span className="break-words"><span style={{ color: "var(--accent)" }}>{e.user_name}</span> · {e.text}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function Attention({ o, go }) {
  const items = [
    o.review_queue && { k: "verify", text: `${o.review_queue} undertaking${o.review_queue === 1 ? "" : "s"} awaiting verification` },
    o.xp_pending && { k: "xp", text: `${o.xp_pending} natural xp proof${o.xp_pending === 1 ? "" : "s"} awaiting review` },
    o.cashouts_pending && { k: "cashouts", text: `${o.cashouts_pending} cash out${o.cashouts_pending === 1 ? "" : "s"} pending · ${cad(o.cashouts_pending_cad)} cad` },
    !o.sentence.active && { k: "sentence", text: o.sentence.paused ? "sentence clock is paused" : "sentence clock is not running" },
  ].filter(Boolean);
  return (
    <Panel title="attention queue" testId="attention-queue">
      {!items.length ? <p className="text-xs opacity-60" data-testid="attention-clear">all clear · nothing needs you</p> : (
        <ul className="flex flex-col gap-2">
          {items.map((it) => (
            <li key={it.k}>
              <button onClick={() => go(it.k)} data-testid={`attention-${it.k}`} className="w-full flex items-center justify-between gap-3 px-3 py-3 text-xs transition-colors hover:bg-[var(--bg-primary)]" style={{ border: "1px solid var(--accent)" }}>
                <span className="flex items-center gap-3"><span className="w-2 h-2 animate-pulse" style={{ background: "var(--accent)" }} />{it.text}</span>
                <span style={{ color: "var(--accent)" }}>open →</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

export function Overview({ go }) {
  const [o] = useLoad("/console/overview", 10000);
  if (!ready(o)) return <Gate data={o} testId="overview" />;
  const rev = o.revenue || {};
  const total = Object.values(rev).reduce((a, r) => a + r.total, 0);
  const asks = o.asks || {};
  const ut = o.undertakings || {};
  return (
    <div className="flex flex-col gap-6" data-testid="console-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi testId="kpi-users" label="users" value={o.users} sub={`+${o.users_24h} last 24h`} onClick={() => go("users")} />
        <Kpi testId="kpi-asks" label="open asks" value={asks.open || 0} sub={`${asks.answered || 0} answered · +${o.asks_24h} 24h`} onClick={() => go("asks")} />
        <Kpi testId="kpi-verify" label="awaiting verification" value={o.review_queue} alert={o.review_queue > 0} sub={`${ut.undertaken || 0} in progress · ${ut.open || 0} open`} onClick={() => go("verify")} />
        <Kpi testId="kpi-cashouts" label="pending cash outs" value={o.cashouts_pending} alert={o.cashouts_pending > 0} sub={`${cad(o.cashouts_pending_cad)} cad owed`} onClick={() => go("cashouts")} />
        <Kpi testId="kpi-pot" label="the pot" value={cad(o.sentence.pot_cad)} sub={`${cad(o.donations_cad)} donated to date`} onClick={() => go("sentence")} />
        <Kpi testId="kpi-sentence" label={o.sentence.active ? "sentence · running" : o.sentence.paused ? "sentence · paused" : "sentence · stopped"} value={fmtDuration(o.sentence.remaining_seconds)} sub={`start ${fmtDuration(o.sentence.start_minutes * 60)}`} onClick={() => go("sentence")} />
        <Kpi testId="kpi-revenue" label="revenue · all time" value={cad(total)} sub={`${(rev.provisions?.count || 0)} provision packs`} onClick={() => go("payments")} />
        <Kpi testId="kpi-circulation" label="provisions held by users" value={o.provisions_in_circulation.toLocaleString()} sub={`${o.answers} answers · +${o.answers_24h} 24h`} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7"><Panel title="live platform stream" testId="overview-stream"><ActivityFeed limit={15} compact testId="overview-feed" /></Panel></div>
        <div className="lg:col-span-5 flex flex-col gap-6">
          <Attention o={o} go={go} />
          <Panel title="revenue breakdown" testId="revenue-breakdown">
            {[["provisions", "provision packs"], ["sentence", "reduce / extend"], ["sentence_item", "help / punish"]].map(([k, l]) => (
              <div key={k} className="flex justify-between text-xs py-2" style={{ borderBottom: "1px dashed var(--border)" }}>
                <span className="opacity-70">{l} · {rev[k]?.count || 0}</span><span style={{ color: "var(--accent)" }} data-testid={`revenue-${k}`}>{cad(rev[k]?.total)}</span>
              </div>
            ))}
          </Panel>
        </div>
      </div>
    </div>
  );
}
